/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Form, Typography } from 'antd';

const { Text } = Typography;

const labelClass =
  'block text-[15px] sm:text-[16px] font-semibold text-[#111827] mb-2';

const inputBase =
  'w-full h-[52px] sm:h-[56px] rounded-xl border bg-white px-4 sm:px-5 pr-12 ' +
  'text-[16px] sm:text-[17px] text-[#111827] placeholder:text-[#9CA3AF] ' +
  'outline-none transition';

const focusGreen =
  'focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/20';

const borderDefault = 'border-[#D1D5DB]';

const Required = () => <span className="ml-1 text-[#DC2626]">*</span>;

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10.58 10.58A3 3 0 0012 15a3 3 0 002.42-4.42"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M9.88 5.09A10.46 10.46 0 0112 5c7 0 10 7 10 7a18.2 18.2 0 01-4.33 5.06"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M6.61 6.61A18.24 18.24 0 002 12s3 7 10 7c1.03 0 2.02-.15 2.95-.43"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M12 15a3 3 0 100-6 3 3 0 000 6z"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

export default function Identification({
  form,
  error,
}: {
  form: any;
  error?: string | null;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const idRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const idValue = Form.useWatch('value', form);
  const passwordValue = Form.useWatch('password', form);

  useEffect(() => {
    const target = form.getFieldValue('value') ? passwordRef : idRef;
    target.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-5 sm:space-y-6">
      <Form.Item
        name="value"
        className="mb-0"
        rules={[{ required: true, message: 'Identification No. is required' }]}
      >
        <div>
          <label className={labelClass}>
            Identification No.
            <Required />
          </label>

          <input
            ref={idRef}
            value={idValue ?? ''}
            onChange={(e) => form.setFieldValue('value', e.target.value)}
            className={`${inputBase} ${borderDefault} ${focusGreen}`}
            placeholder="Enter your identification number"
            inputMode="text"
            autoComplete="username"
          />
        </div>
      </Form.Item>

      <Form.Item
        name="password"
        className="mb-0"
        rules={[{ required: true, message: 'Password is required' }]}
      >
        <div>
          <label className={labelClass}>
            Password
            <Required />
          </label>

          <div className="relative">
            <input
              ref={passwordRef}
              value={passwordValue ?? ''}
              onChange={(e) => form.setFieldValue('password', e.target.value)}
              type={showPassword ? 'text' : 'password'}
              className={`${inputBase} ${borderDefault} ${focusGreen}`}
              placeholder="Enter your password"
              autoComplete="current-password"
            />

            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((s) => !s)}
              className="
                absolute right-3 top-1/2 -translate-y-1/2
                inline-flex h-9 w-9 items-center justify-center
                rounded-lg text-[#6B7280]
                hover:text-[#111827]
                hover:bg-[#F3F4F6]
                focus:outline-none focus:ring-2 focus:ring-[#16A34A]/25
              "
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>
        </div>
      </Form.Item>

      <Form.Item name="remember" valuePropName="checked" className="mb-0">
        <Checkbox>
          <span className="text-[14px] text-[#374151]">
            Remember Me
          </span>
        </Checkbox>
      </Form.Item>

      {error && (
        <Text type="danger" className="block text-[13px] sm:text-[14px]">
          {error}
        </Text>
      )}
    </div>
  );
}
